import Cookies from 'js-cookie';
import { merge } from 'lodash';
import { requestRoot } from '@/axios';
import { getTokenExpireDate } from '@/helpers/auth';

function useState() {
  return {
    parent: null,
    parentChildren: [],
  };
}

const getters = {
  parentAccount: state => state.parent,
  parentChildren: state => state.parentChildren,
};

const actions = {
  async loadParent(context, parent) {
    const parentId = parent?._id || context.getters.parentAccount?._id || Cookies.getJSON('parent')?._id;
    const parentToken = context.getters.parentAccount?.jwt;

    if (!parentId) {
      throw new Error('Parent id is not provided to load parent account');
    }
    if (!parentToken) {
      throw new Error('Failed to load parent account data. Parent token missed');
    }

    try {
      const headers = { Authorization: `Bearer ${parentToken}` };
      const { data } = await requestRoot.get(`/user/${parentId}`, { headers });
      if (data?.user) {
        await context.dispatch('setParentData', data.user);
        await context.dispatch('setParentChildren', data.children);
        return data;
      }

      await context.dispatch('logoutParent');
    } catch (err) {
      if ([401, 403].includes(err.response?.status)) {
        return context.dispatch('logoutParent');
      }
    }
  },
  async loginParent({ dispatch }, { email, password, rememberMe }) {
    const res = await requestRoot.post('/login', {
      email,
      password,
      remember_me: rememberMe,
    });
    dispatch('setParentData', res.data.user);
    return res;
  },
  logoutParent({ dispatch }) {
    dispatch('setParentData', null);
  },
  setParentData(context, parentAccount) {
    context.commit('SET_PARENT_ACCOUNT', parentAccount || null);
    context.dispatch('setParentCookie', parentAccount);

    if (!parentAccount) {
      context.dispatch('setParentChildren', []);
    } else if ((parentAccount.children?.length === 0) || parentAccount.children?.[0]?.id) {
      context.dispatch('setParentChildren', parentAccount.children);
    }
  },
  setParentCookie(_, user) {
    if (!user) {
      const cookieOptions = { path: '/', domain: process.env.VUE_APP_COOKIES_URL };
      Cookies.remove('parent', cookieOptions);
      return;
    }

    const expires = getTokenExpireDate(user.jwt);
    const cookieData = {
      _id: user.id || user._id,
      email: user.email,
      jwt: user.jwt,
      is_confirmed: user.is_confirmed ?? true,
    };
    const cookieOptions = { domain: process.env.VUE_APP_COOKIES_URL, expires };
    Cookies.set('parent', JSON.stringify(cookieData), cookieOptions);
  },
  setParentChildren({ commit }, children = []) {
    commit('SET_PARENT_CHILDREN', children);
  },
  sendRecoveryCode(_, email) {
    return requestRoot.post('/email/confirm/resend', { email });
  },
  recoverPassword(_, { email, code, password }) {
    return requestRoot.post('/password/recovery', {
      email,
      code,
      password,
    });
  },
  updateParentChild({ commit }, { profileId, update }) {
    commit('UPDATE_CHILD', { profileId, update });
  },
};

const mutations = {
  SET_PARENT_ACCOUNT(state, parentAccount) {
    state.parent = parentAccount;
  },
  SET_PARENT_CHILDREN(state, children) {
    state.parentChildren = children;
  },
  UPDATE_CHILD(state, { profileId, update }) {
    const child = state.parentChildren.find(c => c.id === profileId);
    if (child) {
      merge(child, update);
    }
  },
};

export default {
  namespaced: true,
  state: useState,
  getters,
  actions,
  mutations,
};
